import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import { Typography, Grid, TextField } from '@material-ui/core'
import { Tabs, Tab, Box } from '@mui/material'
import Autocomplete from '@material-ui/lab/Autocomplete'
import LambdaFetch from '../functions/FetchFromLambda'
import useDateRangeCol from '../hooks/useDateRangeCol'
import { DashboardAthenaTable } from '../components/DashboardAthenaTable'
import {
  DEFAULT_METADATA_SOURCE,
  getMetadataSystem,
  byMetadataConfigOrder,
  categorySource
} from '../config/metadata-systems'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    margin: '2rem 1rem'
  }
}))

export default function MetadataHelp(props) {
  const classes = useStyles()

  const [allowedCategories, setAllowedCategories] = useState([])
  const [visibleSystems, setVisibleSystems] = useState([])
  const [systemTabIndex, setSystemTabIndex] = useState(0)
  const [loaded, setLoaded] = useState(false)

  const { credentials } = props.fetchInitialData

  useEffect(() => {
    fetchMetaHrd()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const metadataCategoryName = category => category.NAME || category.name

  const fetchMetaHrd = async () => {
    try {
      const resp = await LambdaFetch(
        'reports-athena',
        'post',
        credentials.user.accessToken,
        JSON.stringify({
          action: 'get-config-file',
          file_name: 'metadata-headers.json'
        }),
        '',
        credentials
      )
      const cat = (resp?.data?.metadataCateogries || []).sort((a, b) => {
        if (a.DESCRIPTION < b.DESCRIPTION) return -1
        if (a.DESCRIPTION > b.DESCRIPTION) return 1
        return 0
      })

      const allow = props?.fetchInitialData?.credentials?.allow || (() => false)
      const allowMeta = category => {
        const categoryName = metadataCategoryName(category)
        if (!categoryName) return false

        return allow('metadata', categorySource(category), categoryName)
      }
      const allowed = cat.filter(allowMeta)

      // Distinct erp_source values among allowed categories (in first-seen order),
      // resolved to display config and sorted by METADATA_SYSTEMS order.
      // Categories with no erp_source fall back to DEFAULT_METADATA_SOURCE, so an
      // older metadata-headers.json collapses to a single source (no tabs).
      const seen = new Set()
      const sources = []
      allowed.forEach(c => {
        const src = categorySource(c)
        if (!seen.has(src)) {
          seen.add(src)
          sources.push(src)
        }
      })

      const systems = sources.map(getMetadataSystem).sort(byMetadataConfigOrder)

      setAllowedCategories(allowed)
      setVisibleSystems(systems)
    } catch (e) {
      console.log(e)
    } finally {
      setLoaded(true)
    }
  }

  const categoriesForSystem = system =>
    allowedCategories.filter(c => categorySource(c) === system.sourceKey)

  const handleSystemTabChange = (_, val) => setSystemTabIndex(val)

  return (
    <div style={{ padding: '1.5rem 0.5rem' }}>
      <Typography variant='h5' style={{ textAlign: 'center' }}>
        Metadata
      </Typography>
      <div className={classes.root}>
        {loaded && visibleSystems.length === 0 && (
          <div style={{ textAlign: 'center', marginTop: '2rem', color: '#666' }}>
            You don't have access to any metadata categories.
          </div>
        )}

        {/* ERP system tabs — only rendered when 2+ sources are visible */}
        {visibleSystems.length >= 2 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={systemTabIndex}
              onChange={handleSystemTabChange}
              textColor='primary'
              indicatorColor='primary'
            >
              {visibleSystems.map((sys, idx) => (
                <Tab key={sys.sourceKey} label={sys.label} value={idx} />
              ))}
            </Tabs>
          </Box>
        )}

        {visibleSystems.map((sys, idx) =>
          systemTabIndex === idx ? (
            <MetadataPanel
              key={sys.sourceKey}
              {...props}
              system={sys}
              categories={categoriesForSystem(sys)}
            />
          ) : null
        )}
      </div>
    </div>
  )
}

/**
 * Renders the category picker + results table for a single ERP source.
 *
 * Keyed by sourceKey upstream so it remounts when the active tab changes. That
 * keeps the column set — and therefore the useDateRangeCol hook order — stable
 * for the lifetime of the mount, even though the number of date columns can
 * differ between ERP systems.
 */
function MetadataPanel(props) {
  const { system, categories } = props
  const [category, setCategory] = useState(null)

  // Build base columns from the system config. Date columns use the date-range
  // hook; the column set is constant for this mount (see component doc above).
  const baseCols = system.columns.map(col => {
    if (col.type === 'date') {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useDateRangeCol(col.accessorKey, col.header, false, true)
    }
    return {
      accessorKey: col.accessorKey,
      header: col.header,
      filterFn: col.filterFn || 'contains',
      ...(col.innerSearch ? { innerSearch: true } : {}),
      ...(col.enableSorting ? { enableSorting: true } : {})
    }
  })

  const metaCols = [...baseCols]
  if (category) {
    ;['KEY1', 'KEY2', 'KEY3'].forEach(k => {
      const configuredIndex = metaCols.findIndex(
        col => col.accessorKey?.toUpperCase() === k
      )
      if (category[k] && configuredIndex !== -1) {
        metaCols[configuredIndex] = {
          ...metaCols[configuredIndex],
          header: formatString(category[k])
        }
      }
      if (category[k] && configuredIndex === -1) {
        metaCols.push({
          accessorKey: k,
          header: formatString(category[k]),
          filterFn: 'contains',
          enableSorting: true
        })
      }
    })
  }

  const onTagChange = (event, value) => {
    setCategory(value || null)
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Autocomplete
          id={`category-${system.sourceKey}`}
          options={categories}
          getOptionLabel={option => option.DESCRIPTION || ''}
          filterSelectedOptions
          autoHighlight
          autoSelect
          onChange={onTagChange}
          style={{ width: 300 }}
          renderInput={params => (
            <TextField
              {...params}
              margin='dense'
              label='Category'
              variant='outlined'
            />
          )}
        />
        <div style={{ margin: '1rem 0' }}>
          {!!category && (
            <DashboardAthenaTable
              {...props}
              isTempTable={true}
              key={`${system.sourceKey}-${category.NAME}`}
              params={{ category: category.NAME }}
              tableColumns={metaCols}
              reportParamName={system.paramName || 'meta_data_read_temp'}
              reportName={system.namedQuery}
              tableTitle={''}
              transform={null}
              callback={null}
              downloadFileName={categoryFileName(category)}
            />
          )}
        </div>
      </Grid>
    </Grid>
  )
}

const formatString = str => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Builds a download file name from the selected category's description, e.g.
// "AP Vendor Master" -> "Ap_Vendor_Master". The backend appends its own
// "_download" suffix (see athena-query-serverside-filter.js), so it isn't
// added here to avoid a duplicated "download" in the final file name.
const categoryFileName = category => {
  const label = category?.DESCRIPTION || category?.NAME || category?.name
  if (!label) return 'Metadata'

  return label
    .trim()
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('_')
}
